import { Box, Chip, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel, IconButton, Stack, Typography, Divider, Alert } from "@mui/material";
import {
  ContentCopy,
  EmailOutlined,
  SubjectOutlined,
  DescriptionOutlined,
  CloseOutlined,
} from "@mui/icons-material";
import { ClearIcon } from "@mui/x-date-pickers";
import { useNotification } from "Modules/Core/Notification";
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import { useEffect, useState, useRef } from "react";
import { ShDialog } from "shared/SharedStyles/ShFeedback";
import { ShButton, ShGreenBtn, ShTextFieldV2, ShTextareaWrapper } from "shared/SharedStyles/ShInputs";
import { ShPaper } from "shared/SharedStyles/ShSurfaces";
import { EmailRegEx } from "shared/constants";
import { inviteApplicants, resetInviteApplicants } from "store/slices/employer/applicants/applicants-list-actions-slice";
import { fetchJobSummary } from "store/slices/employer/jobs/jobs-list-slice";
import { IInviteApplicants, IInviteApplicantsPayload } from "./ApplicantsModel";

export const InviteApplicants = ({ isInviteApplicantsOpen, setIsInviteApplicantsOpen, job, initialEmails = "" }: IInviteApplicants) => {
  const dispatch = useAppDispatch();
  const { employerDetails } = useAppSelector(state => state.employer.employerDetails);
  const { jobsSummary } = useAppSelector(state => state.employer.employerJobs.jobsList);
  const { inviteApplicantsStatus, inviteApplicantsResponse } = useAppSelector(state => state.employer.applicants.applicantsListActions);
  const notification = useNotification();

  const [toEmails, setToEmails] = useState<string>(initialEmails);
  const [areEmailsInvalid, setAreEmailsInvalid] = useState<boolean>(false);
  const [isUrlCopied, setIsUrlCopied] = useState<boolean>(false);
  const [inviteApplicantsPayload, setInviteApplicantsPayload] = useState<IInviteApplicantsPayload>({
    body: 'Hi! We invite you to apply to our job. We think you would be a great fit!',
    subject: `${employerDetails?.first_name} ${employerDetails?.last_name}, from ${employerDetails?.company_name}, has invited you to apply for ${job?.name} job`,
    to: ''
  });

  const prevJobId = useRef<string | null>(null);

  useEffect(() => {
    if (job?.id && isInviteApplicantsOpen && job.id.toString() !== prevJobId.current && !jobsSummary?.[job.id]) {
      dispatch(fetchJobSummary({ jobId: Number(job.id) }));
      prevJobId.current = job.id.toString();
    }
  }, [job?.id, isInviteApplicantsOpen, jobsSummary, dispatch]);

  const onEmailsChange = (value: string) => {
    setToEmails(value);
    const emails = value.split(',').map(email => email.trim());
    const regX = new RegExp(EmailRegEx);
    const isValidEmails = emails.every(email => regX.test(email));
    setAreEmailsInvalid(!isValidEmails);
  };

  const sendInvite = () => {
    const payload = { ...inviteApplicantsPayload, to: toEmails };
    dispatch(inviteApplicants({ jobId: job.id, body: payload }));
  };

  const handleCopyUrl = () => {
    const inviteUrl = jobsSummary?.[job?.id]?.inviteUrl || '';
    if (inviteUrl) {
      navigator.clipboard.writeText(inviteUrl)
        .then(() => {
          setIsUrlCopied(true);
          setTimeout(() => setIsUrlCopied(false), 3000);
        })
        .catch((err) => {
          console.error("Failed to copy the URL", err);
        });
    }
  };

  useEffect(() => {
    if (inviteApplicantsStatus === 'success' || inviteApplicantsStatus === 'failed') {
      notification.displayNotification({
        message: inviteApplicantsResponse ?? '',
        open: true,
        type: inviteApplicantsStatus === 'success' ? 'success' : 'error'
      });
      setIsInviteApplicantsOpen(false);
      dispatch(resetInviteApplicants());
    }
  }, [dispatch, inviteApplicantsResponse, inviteApplicantsStatus, notification, setIsInviteApplicantsOpen]);

  return (
    <ShDialog maxWidth="md" open={isInviteApplicantsOpen} onClose={(e) => setIsInviteApplicantsOpen(false)} onClick={e => e.stopPropagation()} scroll="paper" aria-labelledby="job-desc-title">
      <DialogTitle id="title" textAlign="center" sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">Invite Applicants</Typography>
        <Typography variant="subtitle2" color="text.secondary">Share this job opportunity with potential candidates</Typography>
        <IconButton className="close-btn" onClick={() => setIsInviteApplicantsOpen(false)}><ClearIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack rowGap={2}>
          <FormControl fullWidth>
            <FormLabel id="invite_url" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ContentCopy fontSize="small" />
              <Typography component="span" variant="subtitle2">Job Application URL</Typography>
            </FormLabel>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                <a href={jobsSummary?.[job?.id]?.inviteUrl || '#'} target="_blank" rel="noopener noreferrer" style={{ color: '#42a5f5' }}>
                  {jobsSummary?.[job?.id]?.inviteUrl || 'Loading...'}
                </a>
              </Typography>
              <IconButton onClick={handleCopyUrl} size="small" sx={{ bgcolor: 'white' }}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Stack>
            {isUrlCopied && <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>✓ URL copied to clipboard!</Typography>}
            <Alert severity="warning" sx={{ mt: 1 }}>
              To test the job application process, please open this URL in an incognito window or a different browser than the one you are currently logged into.
            </Alert>
          </FormControl>

          <Divider />

          <FormControl fullWidth>
            <FormLabel id="to_emails" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EmailOutlined fontSize="small" />
              <Typography component="span" variant="subtitle2">Candidate Emails</Typography>
            </FormLabel>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              You can invite up to 25 candidates at once. Each candidate will receive a personalized email with the job details and application link.
            </Typography>
            <ShTextFieldV2 
              size="small"
              id="to_emails" 
              fullWidth 
              placeholder="Enter email addresses (comma-separated)" 
              variant="outlined" 
              value={toEmails} 
              onChange={(e) => onEmailsChange(e.target.value)}
              helperText="Separate multiple email addresses with commas"
            />
            {toEmails?.length > 4 && areEmailsInvalid && 
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>Please enter valid email addresses</Typography>
            }
            {toEmails.split(',').length > 25 && 
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>Maximum 25 email addresses allowed</Typography>
            }
          </FormControl>

          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
            {toEmails.split(',').slice(0, 25).map(email => email.trim()).filter(email => email.length > 0).map((email, index) => (
              <Chip size="small" key={index} label={email} color="primary" variant="outlined" />
            ))}
          </Stack>

          <Divider />

          <FormControl fullWidth>
            <FormLabel id="subject_line" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SubjectOutlined fontSize="small" />
              <Typography component="span" variant="subtitle2">Email Subject</Typography>
            </FormLabel>
            <ShTextareaWrapper>
              <textarea 
                id="subject" 
                value={inviteApplicantsPayload.subject} 
                onChange={(e) => setInviteApplicantsPayload({ ...inviteApplicantsPayload, subject: e.target.value })}
                placeholder="Enter a compelling subject line for your invitation email"
              />
            </ShTextareaWrapper>
          </FormControl>

          <FormControl fullWidth>
            <FormLabel id="email_body" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DescriptionOutlined fontSize="small" />
              <Typography component="span" variant="subtitle2">Email Message</Typography>
            </FormLabel>
            <ShTextareaWrapper>
              <textarea 
                id="email_body" 
                value={inviteApplicantsPayload.body} 
                onChange={(e) => setInviteApplicantsPayload({ ...inviteApplicantsPayload, body: e.target.value })}
                placeholder="Write a personalized message to engage potential candidates"
              />
            </ShTextareaWrapper>
          </FormControl>

          <ShGreenBtn 
            onClick={sendInvite} 
            disabled={inviteApplicantsStatus === 'pending' || areEmailsInvalid || toEmails.split(',').length > 25}
            startIcon={<EmailOutlined />}
          >
            {inviteApplicantsStatus === 'pending' ? 'Sending Invitations...' : 'Send Invitation Emails'}
          </ShGreenBtn>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Email Footer</Typography>
            <ShPaper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>POWERED BY SMOOTHHIRING</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>Privacy Policy: http://www.SmoothHiring.com/terms/privacy-policy/</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>Technical issues? info@smoothhiring.com</Typography>
              <Typography variant="body2" color="text.secondary">SmoothHiring, Suite #106, 6797 N High Street, Worthington, Ohio 43085 | info@smoothhiring.com</Typography>
            </ShPaper>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <ShButton 
          variant="outlined" 
          disableElevation 
          size="small" 
          onClick={(e) => { setIsInviteApplicantsOpen(false); e.stopPropagation(); }}
          startIcon={<CloseOutlined />}
        >
          Cancel
        </ShButton>
      </DialogActions>
    </ShDialog>
  );
};
  